import { authApi } from '~/entities/session/api/auth'
import { extractApiError } from '~/shared/lib/api-errors'
import { storageManager } from '~/shared/lib/storage-manager'
import { supabase } from '~/shared/lib/supabase'

export type SessionValidation = 'none' | 'ok' | 'cleared'

/**
 * Drop all local auth state for a dead session: the Supabase token (local scope -
 * a revoke call would just 401) plus the app's business/admin keys, so no stale
 * data survives into the /login state.
 */
async function clearDeadSession(): Promise<void> {
  await supabase.auth.signOut({ scope: 'local' })
  storageManager.clearSessionData()
}

/**
 * Validate a persisted Supabase session before the app renders against it.
 *
 * `getSession()` only reads local storage, so a token left over from an earlier
 * deploy/backend - structurally unexpired but no longer accepted - is kept, and
 * every request then 401s, which surfaced as a blank page after the token-handling
 * change. We prove the token is live via the two auth-critical calls (refresh +
 * `me`) and clear it when it is not, so the app lands on /login (or /upgrade).
 *
 * Only these two calls' 401s clear the session - a random route's 401 (a
 * permission failure) never signs the user out.
 */
export async function validatePersistedSession(): Promise<SessionValidation> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session) return 'none'

  // A dead/rotated refresh token can never mint a usable access token. The auth
  // server rejects it with a 4xx (400 invalid_grant / 401); a network error has
  // no status and must NOT sign the user out.
  const { error: refreshError } = await supabase.auth.refreshSession()
  const refreshStatus = refreshError?.status
  if (refreshStatus === 400 || refreshStatus === 401) {
    await clearDeadSession()
    return 'cleared'
  }

  try {
    await authApi.getCurrentUser()
    return 'ok'
  } catch (error) {
    if (extractApiError(error).statusCode === 401) {
      await clearDeadSession()
      return 'cleared'
    }
    // Anything else keeps the session: BusinessRoleRequired means a valid personal
    // account (login and the /upgrade route own that routing - the boot gate must
    // not redirect, or it would yank users off recovery/invite links), and a
    // transient network/5xx must not discard a possibly-good session.
    return 'ok'
  }
}
